import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getNumberHealthApiV1NumberHealthPost } from '#/shared/api/generated/communication/theCargoCommunication'
import type { NumberHealthRead } from '#/shared/api/generated/communication/models'
import { usPhoneToE164 } from '#/shared/lib/format'

/**
 * Registration is split from the data on purpose. The registry is stable for the
 * provider's whole life, so a badge registers its phone exactly once and its
 * effect never re-runs when new health data arrives; only the (separate) data
 * context changes as batches resolve. Bundling them (one context value that
 * changes on every fetch) made every row's register effect re-fire per update —
 * the source of the row lag.
 */
interface NumberHealthRegistry {
  register: (phone: string) => void
  unregister: (phone: string) => void
}

const RegistryContext = createContext<NumberHealthRegistry | null>(null)
const DataContext = createContext<Map<string, NumberHealthRead>>(new Map())
// Phones we've heard back on (see `useNumberHealthMap`). Lets a consumer tell an
// authoritative "healthy" (resolved, absent from the data map) from "not fetched
// yet" — both of which read as `undefined` from the data context alone.
const StatusContext = createContext<Set<string>>(new Set())

// The registry churns as rows mount/unmount during a paint or a page change;
// waiting a beat collapses that into a single batch request per settled screen.
const BATCH_DEBOUNCE_MS = 150
const HEALTH_STALE_MS = 5 * 60 * 1000

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i])
}

// DEV-ONLY preview toggle — lets you see every badge/banner state in the real
// app without waiting on backend delivery data. In the browser console run e.g.
//   localStorage.numberHealthForce = 'landline'   // or optedOut | invalid | healthy
// then reopen the chat / reload; `delete localStorage.numberHealthForce` to stop.
// Stripped from production by `import.meta.env.DEV`. Remove before shipping.
const DEV_FORCED: Record<
  string,
  Omit<NumberHealthRead, 'phone' | 'last_verified_at'>
> = {
  healthy: {
    status: 'healthy',
    line_type: 'mobile',
    carrier: 'T-Mobile',
    voice_deliverable: true,
    sms_state: 'deliverable',
  },
  landline: {
    status: 'sms_blocked',
    line_type: 'landline',
    carrier: 'AT&T',
    voice_deliverable: true,
    sms_state: 'not_supported',
  },
  optedOut: {
    status: 'sms_blocked',
    line_type: 'mobile',
    carrier: 'Verizon',
    voice_deliverable: true,
    sms_state: 'opted_out',
    opted_out_at: '2026-03-12T09:00:00Z',
  },
  invalid: {
    status: 'invalid',
    line_type: 'unknown',
    carrier: null,
    voice_deliverable: false,
    sms_state: 'undeliverable',
  },
}

function devForcedMap(phones: string[]): Map<string, NumberHealthRead> | null {
  if (!import.meta.env.DEV) return null
  const key = localStorage.getItem('numberHealthForce') ?? ''
  if (!(key in DEV_FORCED)) return null
  const preset = DEV_FORCED[key]
  const map = new Map<string, NumberHealthRead>()
  for (const phone of phones) {
    map.set(phone, {
      phone,
      last_verified_at: '2026-07-11T14:02:00Z',
      ...preset,
    })
  }
  return map
}

/** One batched request for every phone currently on screen → a lookup map plus
 *  the set of phones the backend has answered for (`resolved`). */
function useNumberHealthMap(phones: string[]): {
  byPhone: Map<string, NumberHealthRead>
  resolved: Set<string>
} {
  const { data, isPlaceholderData, isError } = useQuery({
    queryKey: ['number-health', phones],
    queryFn: async ({ signal }) => {
      const response = await getNumberHealthApiV1NumberHealthPost(
        { phones },
        signal,
      )
      return response.data
    },
    enabled: phones.length > 0,
    staleTime: HEALTH_STALE_MS,
    // Keep the last map while a new page's batch is in flight — badges don't
    // flash out and back on pagination.
    placeholderData: keepPreviousData,
  })

  // Accumulate resolved phones monotonically: once a number has an answer it
  // stays "known", so a later in-flight batch (new page / new row) never reverts
  // it to "loading". An error counts as resolved too — we fall back to the
  // unblocked UI rather than wait on a call that won't come. `data === undefined`
  // while placeholder data is showing means this batch hasn't landed yet.
  const resolvedRef = useRef<Set<string>>(new Set())
  const [resolved, setResolved] = useState<Set<string>>(resolvedRef.current)
  useEffect(() => {
    if (phones.length === 0 || isPlaceholderData) return
    if (data === undefined && !isError) return
    let changed = false
    for (const phone of phones) {
      if (!resolvedRef.current.has(phone)) {
        resolvedRef.current.add(phone)
        changed = true
      }
    }
    if (changed) setResolved(new Set(resolvedRef.current))
  }, [data, isError, isPlaceholderData, phones])

  const byPhone = useMemo(() => {
    const forced = devForcedMap(phones)
    if (forced) return forced
    const map = new Map<string, NumberHealthRead>()
    for (const item of data?.items ?? []) map.set(item.phone, item)
    return map
  }, [data, phones])

  return { byPhone, resolved }
}

/**
 * Demo-only for now: the production lookup data has been flagging reachable
 * numbers as blocked, so prod doesn't fetch per-number status from this API.
 * Without a mounted provider every lookup consumer no-ops: fallback badges and
 * banners render nothing and the composer never holds or blocks a send. This
 * gate covers ONLY the batched number-health lookup — the fallback for
 * customer-less conversations. Known customers carry `sms_deliverable` /
 * `deliverability_reason` inline on the customer object, and that UI
 * (CustomerSmsBadge, useDeliverabilityBanner's inline path) renders in every
 * environment, production included.
 */
const NUMBER_HEALTH_ENABLED =
  import.meta.env['VITE_SENTRY_ENVIRONMENT'] !== 'production'

/**
 * Collects every phone rendered under it (badges, the conversation header, the
 * composer) and resolves their deliverability in one batched call — the ticket's
 * "batch once per page, never per row". Mounted once at the dashboard shell so a
 * list page and the phone panel share a single request.
 */
export function NumberHealthProvider({
  children,
}: {
  children: React.ReactNode
}) {
  if (!NUMBER_HEALTH_ENABLED) return children
  return <EnabledNumberHealthProvider>{children}</EnabledNumberHealthProvider>
}

function EnabledNumberHealthProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const counts = useRef(new Map<string, number>())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [phones, setPhones] = useState<string[]>([])

  const recompute = useCallback(() => {
    const next = Array.from(counts.current.keys()).sort()
    setPhones((prev) => (sameList(prev, next) ? prev : next))
  }, [])

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(recompute, BATCH_DEBOUNCE_MS)
  }, [recompute])

  const register = useCallback(
    (phone: string) => {
      counts.current.set(phone, (counts.current.get(phone) ?? 0) + 1)
      schedule()
    },
    [schedule],
  )

  const unregister = useCallback(
    (phone: string) => {
      const next = (counts.current.get(phone) ?? 0) - 1
      if (next <= 0) counts.current.delete(phone)
      else counts.current.set(phone, next)
      schedule()
    },
    [schedule],
  )

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const { byPhone, resolved } = useNumberHealthMap(phones)

  // Stable for the provider's lifetime → registration effects run once per phone.
  const registry = useMemo<NumberHealthRegistry>(
    () => ({ register, unregister }),
    [register, unregister],
  )

  return (
    <RegistryContext.Provider value={registry}>
      <StatusContext.Provider value={resolved}>
        <DataContext.Provider value={byPhone}>{children}</DataContext.Provider>
      </StatusContext.Provider>
    </RegistryContext.Provider>
  )
}

/**
 * Deliverability for one phone. Registers it with the nearest provider's batch
 * and returns the record once the batch resolves (`undefined` until then, or
 * when there is no provider — callers render nothing in that case). Accepts any
 * display format; it normalizes to E.164 to match the batch response.
 */
export function useNumberHealth(
  phone?: string | null,
): NumberHealthRead | undefined {
  const registry = useContext(RegistryContext)
  const data = useContext(DataContext)
  const e164 = phone ? usPhoneToE164(phone) : null

  useEffect(() => {
    if (!registry || !e164) return
    registry.register(e164)
    return () => registry.unregister(e164)
  }, [registry, e164])

  return e164 ? data.get(e164) : undefined
}

/**
 * Force a fresh deliverability fetch. Invalidating the batch query refetches the
 * status for every on-screen phone in one request — called when a chat opens so
 * the composer reflects the latest status rather than a cached one.
 */
export function useRefreshNumberHealth(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['number-health'] })
  }, [queryClient])
}

/**
 * Whether a phone's deliverability is settled — the batch has answered for it,
 * so `useNumberHealth` returning `undefined` now means "healthy", not "pending".
 * Lets the composer hold its input until it knows whether to swap in the blocked
 * banner, instead of flashing the input and then replacing it. Returns `true`
 * (nothing to wait on) for a blank number or when no provider is mounted.
 */
export function useNumberHealthResolved(phone?: string | null): boolean {
  const registry = useContext(RegistryContext)
  const resolved = useContext(StatusContext)
  const e164 = phone ? usPhoneToE164(phone) : null
  if (!e164 || !registry) return true
  return resolved.has(e164)
}
